/** Maintenance & Compliance (Demo Mode only) - the planned maintenance, inspection and
 * compliance calendar for estate assets: scheduled maintenance, safety inspections, statutory
 * checks, warranty expiry, certification, service provider visits. No backend
 * maintenance/compliance engine exists yet - all data is static mock data, matching the same
 * "seeded illustrative numbers" approach used by src/pages/EstateManagement. */

import { useState } from 'react';
import { Box, Chip, Grid, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { ErrorOutline, Schedule, EventAvailable, FactCheck } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';

const MOCK_SCHEDULE = [
  { site: 'St Mary\'s School', asset: 'TF-STMARY-KIT-0042', type: 'Planned Service', dueDate: '2026-11-12', supplier: 'CaterCare Services Ltd', status: 'Scheduled' },
  { site: 'Riverside', asset: 'TF-RVSD-STR-0003', type: 'Statutory Inspection', dueDate: '2026-06-10', supplier: 'CaterCare Services Ltd', status: 'Due Soon' },
  { site: 'Uptown', asset: 'TF-UPTN-BAR-0022', type: 'Safety Inspection', dueDate: '2026-05-25', supplier: 'La Marzocco UK', status: 'Overdue' },
  { site: 'Downtown', asset: 'TF-DWTN-KIT-0014', type: 'Warranty Expiry', dueDate: '2026-08-19', supplier: '-', status: 'Scheduled' },
  { site: 'Southgate', asset: 'TF-SGTE-KIT-0009', type: 'Planned Service', dueDate: '2026-06-01', supplier: 'CaterCare Services Ltd', status: 'Due Soon' },
];

const COMPLIANCE_BY_CATEGORY = [
  { category: 'Cooking', compliant: 12, dueSoon: 2, overdue: 1 },
  { category: 'Refrigeration', compliant: 9, dueSoon: 1, overdue: 1 },
  { category: 'Beverage', compliant: 6, dueSoon: 0, overdue: 1 },
  { category: 'Warewashing', compliant: 5, dueSoon: 1, overdue: 0 },
];

const statusColour = (status: string) => {
  if (status === 'Overdue') return 'error';
  if (status === 'Due Soon') return 'warning';
  return 'success';
};

const MaintenanceCompliance = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [schedulePage, setSchedulePage] = useState(0);
  const [categoryPage, setCategoryPage] = useState(0);

  const overdue = MOCK_SCHEDULE.filter((s) => s.status === 'Overdue').length;
  const dueSoon = MOCK_SCHEDULE.filter((s) => s.status === 'Due Soon').length;

  const TILES = [
    { label: t('maintenanceComplianceOverdue', 'Overdue checks'), value: `${overdue}`, subtitle: t('maintenanceComplianceOverdueSub', 'Needs immediate action'), icon: <ErrorOutline />, color: '#d32f2f' },
    { label: t('maintenanceComplianceDueSoon', 'Due soon'), value: `${dueSoon}`, subtitle: t('maintenanceComplianceDueSoonSub', 'Approaching due date'), icon: <Schedule />, color: '#ed6c02' },
    { label: t('maintenanceComplianceScheduled', 'Scheduled'), value: `${MOCK_SCHEDULE.filter((s) => s.status === 'Scheduled').length}`, subtitle: t('maintenanceComplianceScheduledSub', 'Planned ahead'), icon: <EventAvailable />, color: '#2e7d32' },
    { label: t('maintenanceComplianceTotal', 'Tracked assets'), value: `${MOCK_SCHEDULE.length}`, subtitle: t('maintenanceComplianceTotalSub', 'Under compliance tracking'), icon: <FactCheck />, color: '#1976d2' },
  ];

  return (
    <PageArea>
      <PageTitle>
        {t('maintenanceComplianceTitle', 'Maintenance & Compliance')}
      </PageTitle>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.label}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('maintenanceComplianceScheduleTitle', 'Maintenance & compliance schedule')}
            <Pill text={`${MOCK_SCHEDULE.length} ${t('maintenanceComplianceItemsLabel', 'items')}`} />
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('estateManagementStore', 'Location')}</TableCell>
                  <TableCell>{t('siteEquipmentAssetNumber', 'Asset')}</TableCell>
                  <TableCell>{t('siteEquipmentHistoryType', 'Type')}</TableCell>
                  <TableCell>{t('maintenanceComplianceDueDate', 'Due Date')}</TableCell>
                  <TableCell>{t('siteEquipmentServiceProvider', 'Service Provider')}</TableCell>
                  <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_SCHEDULE.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.site}</TableCell>
                    <TableCell>{row.asset}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.dueDate}</TableCell>
                    <TableCell>{row.supplier}</TableCell>
                    <TableCell>
                      <Chip label={row.status}
                        size="small"
                        color={statusColour(row.status)}
                        variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={schedulePage}
            setPage={setSchedulePage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('maintenanceComplianceByCategoryTitle', 'Compliance by equipment category')}
            <Pill text={`${COMPLIANCE_BY_CATEGORY.length} ${t('estateManagementCategoriesLabel', 'categories')}`} />
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('estateManagementCategory', 'Category')}</TableCell>
                  <TableCell>{t('maintenanceComplianceCompliant', 'Compliant')}</TableCell>
                  <TableCell>{t('maintenanceComplianceDueSoon', 'Due soon')}</TableCell>
                  <TableCell>{t('maintenanceComplianceOverdue', 'Overdue')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {COMPLIANCE_BY_CATEGORY.map((row) => (
                  <TableRow key={row.category}>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>{row.compliant}</TableCell>
                    <TableCell>{row.dueSoon}</TableCell>
                    <TableCell>{row.overdue}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={categoryPage}
            setPage={setCategoryPage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>
    </PageArea>
  );
};

export default MaintenanceCompliance;
